import React, { useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MDTypography } from '../../components/MDTypography';
import { Bill } from './ClaimDetailsTypes';
import { ServiceLinesView } from './ServiceLinesView';
import PDFViewer from 'components/PDFViewer/PDFViewer';
import { ContextPanel } from 'components/ContextPanel';
import {
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  SaveAlt as DownloadExcelIcon,
} from '@material-ui/icons';
import { MDButton } from 'components/MDButton';
import { getSignedUrl } from 'services/storageService';
import { downloadDiscountDetailsPdf } from 'services/billsService';
import { logger } from 'services/logger';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '100%',
    '& .MuiCard-root': {
      marginBottom: theme.spacing(2),
    },
  },
  billHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  infoGrid: {
    '& .MuiGrid-item': {
      marginBottom: theme.spacing(1),
    },
  },
  labelText: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  valueText: {
    color: theme.palette.text.primary,
  },
  noForms: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

interface BillDetailsViewProps {
  bill: Bill;
}

export const BillDetailsView: React.FC<BillDetailsViewProps> = ({ bill }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isDownloadingDiscountPdf, setIsDownloadingDiscountPdf] =
    useState(false);
  const [discountPdfError, setDiscountPdfError] = useState<string | null>(null);
  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleDownload = async (formType: string) => {
    if (!bill.fileUrl) return;
    const url = await getSignedUrl(bill.fileUrl, 'bill');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formType}_${bill.gemBillNo}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showPdfViewer = useCallback(async (docUrl, type) => {
    try {
      const url = await getSignedUrl(docUrl, type);
      setPdfUrl(url);
      setPdfViewerOpen(true);
    } catch (error) {
      logger.error('Error fetching signed URL for PDF viewer', {
        error: error as Error,
      });
    }
  }, []);

  const handleDownloadDiscountPdf = async () => {
    setIsDownloadingDiscountPdf(true);
    setDiscountPdfError(null);
    try {
      const billNo = bill.gemBillNo || 'unknown';
      const blob = await downloadDiscountDetailsPdf(billNo);
      // Guard: an empty blob means the server returned nothing useful (e.g. a
      // non-2xx response body that axios swallowed, or a zero-byte PDF).
      if (blob.size === 0) {
        throw new Error('PDF generation returned an empty file');
      }

      // const blobUrl = URL.createObjectURL(blob);
      // const anchor = document.createElement('a');
      // anchor.href = blobUrl;
      // anchor.download = `discount-details-${billNo}.pdf`;
      // anchor.style.display = 'none';
      // document.body.appendChild(anchor);
      // anchor.click();
      // document.body.removeChild(anchor);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `discount-details-${billNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // 5 000 ms gives every browser (including Firefox on slow machines) ample
      // time to read the blob data and queue the download before the URL handle
      // is released.  100 ms was too aggressive and could silently produce empty
      // or corrupted files when the browser was under load.
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      logger.error('Error downloading discount details PDF', {
        error: error as Error,
      });
      setDiscountPdfError(t('Failed to generate discount details PDF'));
    } finally {
      setIsDownloadingDiscountPdf(false);
    }
  };

  const renderBillInfo = () => (
    <Card>
      <CardHeader
        className={classes.billHeader}
        title={
          <MDTypography variant="h6" fontWeight="medium">
            {t('Bill Information')}
          </MDTypography>
        }
        subheader={
          <>
            <MDTypography variant="body2" color="text">
              {`${t('Bill ID')}: ${bill.gemBillNo} | ${t('Type')}: ${
                bill.billType
              }`}
            </MDTypography>
            {(bill.fileUrl || bill.discountDetailsEligible) && (
              <div
                style={{
                  paddingTop: '16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                {bill.fileUrl && (
                  <>
                    <MDButton
                      variant="gradient"
                      color="info"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      onClick={() => showPdfViewer(bill.fileUrl, 'bill')}
                    >
                      <MDTypography variant="caption" color="white">
                        {t('View Bill PDF')}
                      </MDTypography>
                      {/* aria-hidden: icon is decorative; button label is already in the text node */}
                      <ViewIcon aria-hidden="true" />
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      onClick={() => handleDownload('CMS1500')}
                    >
                      <MDTypography variant="caption" color="white">
                        {t('Download Bill PDF')}
                      </MDTypography>
                      <DownloadIcon aria-hidden="true" />
                    </MDButton>
                  </>
                )}
                {/* Discount Details button is independent of fileUrl — the PDF is
                    generated server-side using only the bill ID. */}
                {bill.discountDetailsEligible === true && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    disabled={isDownloadingDiscountPdf}
                    aria-busy={isDownloadingDiscountPdf}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={handleDownloadDiscountPdf}
                  >
                    <MDTypography variant="caption" color="white">
                      {isDownloadingDiscountPdf
                        ? t('Generating PDF…')
                        : t('Download Discount Details')}
                    </MDTypography>
                    <DownloadIcon aria-hidden="true" />
                  </MDButton>
                )}
                {/* role="alert" ensures screen readers announce this message when it
                    dynamically appears — without it the error is visually shown but
                    never read aloud. */}
                {discountPdfError && (
                  <MDTypography
                    role="alert"
                    variant="caption"
                    color="error"
                    style={{ alignSelf: 'center' }}
                  >
                    {discountPdfError}
                  </MDTypography>
                )}
                {/* <IconButton
                  onClick={handleDownloadExcel}
                  size="small"
                  style={{ color: 'white' }}
                  title={t('Download Excel')}
                >
                  <DownloadExcelIcon />
                </IconButton> */}
              </div>
            )}
          </>
        }
      />
      <CardContent>
        <Grid container spacing={2} className={classes.infoGrid}>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="body2" className={classes.labelText}>
              {t('Provider')}:
            </MDTypography>
            <MDTypography variant="body2" className={classes.valueText}>
              {bill.payableTo}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="body2" className={classes.labelText}>
              {t('Billed Amount')}:
            </MDTypography>
            <MDTypography variant="body2" className={classes.valueText}>
              {formatCurrency(bill.billedCharges)}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="body2" className={classes.labelText}>
              {t('Service Date')}:
            </MDTypography>
            <MDTypography variant="body2" className={classes.valueText}>
              {formatDate(bill.dateOfServiceFrom)} -{' '}
              {formatDate(bill.dateOfServiceTo)}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="body2" className={classes.labelText}>
              {t('Primary Diagnosis')}:
            </MDTypography>
            <MDTypography variant="body2" className={classes.valueText}>
              {bill.primaryDiagnosis}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="body2" className={classes.labelText}>
              {t('Medical Records')}:
            </MDTypography>
            {bill.hipaaWaivers?.medicalRecords.length &&
              bill.hipaaWaivers.medicalRecords.map((record) => (
                <span
                  key={record.docUrl}
                  className={classes.valueText}
                  style={{ cursor: 'pointer' }}
                  onClick={() => showPdfViewer(record.docUrl, 'medical-record')}
                >
                  {record.fileName}
                </span>
              ))}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const hasServiceLines =
    bill.ClaimsBillsServiceLines && bill.ClaimsBillsServiceLines.length > 0;
  // const hasAnyForms = bill.Cms1500Form || bill.Ub04Form;
  const hasAnyDetails = hasServiceLines;

  return (
    <Box className={classes.container}>
      {renderBillInfo()}

      {hasAnyDetails ? (
        <>
          {/* Show service lines if available */}
          {hasServiceLines && (
            <ServiceLinesView
              serviceLines={bill.ClaimsBillsServiceLines!}
              billNo={bill.gemBillNo}
            />
          )}

          {/* Show forms if available */}
          {/* {bill.Cms1500Form && (
            <Cms1500FormView
              form={bill.Cms1500Form}
              billNo={bill.gemBillNo}
              fileUrl={bill.fileUrl}
            />
          )}
          {bill.Ub04Form && (
            <Ub04FormView
              form={bill.Ub04Form}
              billNo={bill.gemBillNo}
              fileUrl={bill.fileUrl}
            />
          )} */}
        </>
      ) : (
        <Card>
          <CardContent className={classes.noForms}>
            <MDTypography variant="h6" color="secondary">
              {t('Bill details unavailable')}
            </MDTypography>
            <MDTypography variant="body2" color="secondary">
              {t(
                'You can view or download the the invoice by clicking the button "View PDF" above.',
              )}
            </MDTypography>
          </CardContent>
        </Card>
      )}
      {pdfUrl && (
        <ContextPanel
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          side="right"
          size="fullsize"
          title={t('PDF Viewer')}
        >
          <PDFViewer pdfUrl={pdfUrl} />
        </ContextPanel>
      )}
    </Box>
  );
};
